import React, { createContext, useContext, useEffect, useState } from 'react'
import { getPasswordPolicy, PasswordPolicy } from '../service/passwordPolicyService'

interface PasswordPolicyContextType {
  passwordPolicy: PasswordPolicy | null
  isLoading: boolean
  error: string | null
}

export const PasswordPolicyContext = createContext<PasswordPolicyContextType | undefined>(undefined)

export const usePasswordPolicy = () => {
  const context = useContext(PasswordPolicyContext)
  if (context === undefined) {
    throw new Error('usePasswordPolicy must be used within a PasswordPolicyProvider')
  }
  return context
}

export const PasswordPolicyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [passwordPolicy, setPasswordPolicy] = useState<PasswordPolicy | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchPasswordPolicy = async () => {
      try {
        setIsLoading(true)
        setError(null)
        const policy = await getPasswordPolicy()
        setPasswordPolicy(policy)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch password policy')
        // Set default policy on error
        setPasswordPolicy({ minimumLength: 15 })
      } finally {
        setIsLoading(false)
      }
    }

    fetchPasswordPolicy()
  }, [])

  return (
    <PasswordPolicyContext.Provider value={{ passwordPolicy, isLoading, error }}>
      {children}
    </PasswordPolicyContext.Provider>
  )
}
